import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import type { DialectId, DuckdbFileEntry, SslMode } from '@bits2schema/shared';
import { api } from '../api/client';
import { useT } from '../i18n';
import { formatBytes, type ConnectionDraft } from '../utils/format';

interface ConnectionFormProps {
  dialect: DialectId;
  draft: ConnectionDraft;
  onChange: (draft: ConnectionDraft) => void;
}

/** E1-US1 / E1-US2 — formulář připojení; jen oddělená pole, žádný connection string. */
export function ConnectionForm({ dialect, draft, onChange }: ConnectionFormProps) {
  const t = useT();
  const update = useCallback(
    (patch: Partial<ConnectionDraft>) => onChange({ ...draft, ...patch }),
    [draft, onChange],
  );

  if (dialect === 'duckdb') {
    return <DuckdbFields draft={draft} update={update} />;
  }

  return (
    <div className="stack">
      <div className="row">
        <div className="field">
          <label htmlFor="conn-host">{t('connection.postgres.host')}</label>
          <input
            id="conn-host"
            value={draft.host}
            onChange={(event) => update({ host: event.target.value })}
          />
        </div>
        <div className="field field--narrow">
          <label htmlFor="conn-port">{t('connection.postgres.port')}</label>
          <input
            id="conn-port"
            inputMode="numeric"
            value={draft.port}
            onChange={(event) => update({ port: event.target.value })}
          />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="conn-database">{t('connection.postgres.database')}</label>
          <input
            id="conn-database"
            value={draft.database}
            onChange={(event) => update({ database: event.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="conn-user">{t('connection.postgres.user')}</label>
          <input
            id="conn-user"
            value={draft.user}
            onChange={(event) => update({ user: event.target.value })}
          />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="conn-password">{t('connection.postgres.password')}</label>
          <input
            id="conn-password"
            type="password"
            autoComplete="off"
            value={draft.password}
            onChange={(event) => update({ password: event.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="conn-ssl">{t('connection.postgres.sslMode')}</label>
          <select
            id="conn-ssl"
            value={draft.sslMode}
            onChange={(event) => update({ sslMode: event.target.value as SslMode })}
          >
            <option value="disable">{t('connection.postgres.sslMode.disable')}</option>
            <option value="require">{t('connection.postgres.sslMode.require')}</option>
            <option value="verify-full">{t('connection.postgres.sslMode.verifyFull')}</option>
          </select>
        </div>
      </div>

      <p className="hint">{t('connection.passwordNotice')}</p>
    </div>
  );
}

function DuckdbFields({
  draft,
  update,
}: {
  draft: ConnectionDraft;
  update: (patch: Partial<ConnectionDraft>) => void;
}) {
  const t = useT();
  const [files, setFiles] = useState<DuckdbFileEntry[]>([]);
  const [rootLabel, setRootLabel] = useState('');

  const loadFiles = useCallback(async () => {
    const response = await api.listDuckdbFiles().catch(() => null);
    if (!response) return;
    setFiles(response.files);
    setRootLabel(response.rootLabel);
  }, []);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  return (
    <div className="stack">
      <div className="field">
        <label htmlFor="conn-file">{t('connection.duckdb.file')}</label>
        {files.length > 0 ? (
          <select
            id="conn-file-select"
            value={files.some((file) => file.path === draft.filePath) ? draft.filePath : ''}
            onChange={(event) => update({ filePath: event.target.value })}
          >
            <option value="">—</option>
            {files.map((file) => (
              <option key={file.path} value={file.path}>
                {`${file.path} (${formatBytes(file.sizeBytes)})`}
              </option>
            ))}
          </select>
        ) : (
          <p className="hint">{t('connection.duckdb.noFiles')}</p>
        )}
      </div>

      <div className="field">
        <input
          id="conn-file"
          value={draft.filePath}
          placeholder={t('connection.duckdb.filePlaceholder')}
          onChange={(event) => update({ filePath: event.target.value })}
        />
      </div>

      <div className="row">
        <p className="hint">{t('connection.duckdb.rootHint', { root: rootLabel })}</p>
        <button type="button" className="link" onClick={() => void loadFiles()}>
          <RefreshCw size={14} />
          {t('connection.duckdb.refresh')}
        </button>
      </div>
    </div>
  );
}
